import React, { useState } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Users, 
  TrendingUp, 
  RefreshCcw, 
  Trophy, 
  Medal, 
  Award,
  ExternalLink,
  UserPlus,
  DollarSign,
  Percent,
  Target,
  ArrowRight,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
  Megaphone,
  RotateCcw
} from 'lucide-react';
import { useReferralsMetrics, ReferrerStats, PendingReferral, ScatterDataPoint, ConversionDetail } from '@/hooks/useReferralsMetrics';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, Label } from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface ReferralsMetricsProps {
  dateRange?: DateRange;
}

// Helper to get source display info
function getSourceDisplay(source: string, isReactivation: boolean): { label: string; icon: React.ReactNode; color: string } {
  if (isReactivation) {
    return { 
      label: 'Reativação', 
      icon: <RotateCcw className="h-3.5 w-3.5" />, 
      color: 'text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400' 
    };
  }
  
  switch (source) {
    case 'Customer Referral':
      return { 
        label: 'Customer Referral', 
        icon: <CheckCircle2 className="h-3.5 w-3.5" />, 
        color: 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400' 
      };
    case 'Meta Ads':
    case 'Facebook Ads':
    case 'Instagram Ads':
      return { 
        label: source, 
        icon: <Megaphone className="h-3.5 w-3.5" />, 
        color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400' 
      };
    case 'Google Ads':
      return { 
        label: 'Google Ads', 
        icon: <Megaphone className="h-3.5 w-3.5" />, 
        color: 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400' 
      };
    case 'Organic':
      return { 
        label: 'Orgânico', 
        icon: <Users className="h-3.5 w-3.5" />, 
        color: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400' 
      };
    default:
      return { 
        label: source || 'Desconhecido', 
        icon: <ArrowRight className="h-3.5 w-3.5" />, 
        color: 'text-muted-foreground bg-muted' 
      };
  }
}

export default function ReferralsMetrics({ dateRange }: ReferralsMetricsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState('top');
  const [expandedReferrers, setExpandedReferrers] = useState<Set<string>>(new Set());
  const { data, isLoading, error } = useReferralsMetrics(dateRange);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const toggleExpanded = (referrerId: string) => {
    setExpandedReferrers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(referrerId)) {
        newSet.delete(referrerId);
      } else {
        newSet.add(referrerId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-6">
          <p className="text-destructive">Erro ao carregar métricas de indicações</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  // Calculate scatter chart bounds
  const maxReferrals = Math.max(...data.effortVsResultData.map(d => d.referrals), 1);
  const maxValue = Math.max(...data.effortVsResultData.map(d => d.deal_value), 1);
  const avgReferrals = data.effortVsResultData.reduce((a, b) => a + b.referrals, 0) / (data.effortVsResultData.length || 1);
  const avgValue = data.effortVsResultData.reduce((a, b) => a + b.deal_value, 0) / (data.effortVsResultData.length || 1);

  // Categorize scatter points into quadrants
  const getQuadrant = (point: ScatterDataPoint) => {
    const highEffort = point.referrals >= avgReferrals;
    const highResult = point.deal_value >= avgValue;
    
    if (highEffort && highResult) return 'super-partners';
    if (highEffort && !highResult) return 'need-help';
    if (!highEffort && highResult) return 'quality';
    return 'inactive';
  };

  const quadrantColors: Record<string, string> = {
    'super-partners': 'hsl(var(--chart-1))',
    'need-help': 'hsl(var(--chart-4))',
    'quality': 'hsl(var(--chart-2))',
    'inactive': 'hsl(var(--muted-foreground))',
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return <Trophy className="h-5 w-5 text-yellow-500" />;
    if (index === 1) return <Medal className="h-5 w-5 text-gray-400" />;
    if (index === 2) return <Award className="h-5 w-5 text-amber-600" />;
    return <span className="w-5 text-center text-muted-foreground">{index + 1}</span>;
  };

  const handleViewContact = (contactId: string) => {
    navigate(`/contactdetail?id=${contactId}`);
  };

  // Render the conversion details table for a referrer
  const renderConversionDetails = (referrer: ReferrerStats, filterType?: 'direct' | 'indirect') => {
    const details = filterType 
      ? referrer.conversion_details.filter(d => d.conversion_type === filterType)
      : referrer.conversion_details;

    if (details.length === 0) {
      return (
        <div className="py-4 text-center text-muted-foreground text-sm">
          Nenhuma conversão {filterType === 'direct' ? 'direta' : filterType === 'indirect' ? 'indireta' : ''} encontrada
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="text-xs">Contato Indicado</TableHead>
            <TableHead className="text-xs">Canal/Origem</TableHead>
            <TableHead className="text-xs text-center">Tipo</TableHead>
            <TableHead className="text-xs text-right">Valor</TableHead>
            <TableHead className="w-8"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {details.map((detail, idx) => {
            const sourceInfo = getSourceDisplay(detail.deal_source, detail.is_reactivation);
            return (
              <TableRow key={`${detail.deal_id}-${idx}`} className="text-sm">
                <TableCell className="font-medium py-2">{detail.referred_name}</TableCell>
                <TableCell className="py-2">
                  <Badge variant="secondary" className={`${sourceInfo.color} gap-1`}>
                    {sourceInfo.icon}
                    {sourceInfo.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-center py-2">
                  {detail.conversion_type === 'direct' ? (
                    <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      Direta
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      Indireta
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right font-medium py-2">
                  {formatCurrency(detail.deal_amount)}
                </TableCell>
                <TableCell className="py-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleViewContact(detail.referred_contact_id)}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Indicações</p>
                <p className="text-2xl font-bold">{data.totalReferrals}</p>
                <p className="text-xs text-muted-foreground">
                  {data.totalContactReferrals} contatos · {data.totalDealReferrals} deals
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Diretas</p>
                <p className="text-2xl font-bold text-green-600">{data.directConversions}</p>
                <p className="text-xs text-green-600/80">
                  {formatCurrency(data.directDealValue)} · comissão
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <RefreshCcw className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Indiretas</p>
                <p className="text-2xl font-bold text-blue-600">{data.indirectConversions}</p>
                <p className="text-xs text-blue-600/80">
                  {formatCurrency(data.indirectDealValue)} · reconhecimento
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-yellow-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pendentes</p>
                <p className="text-2xl font-bold text-yellow-600">{data.pendingConversion}</p>
                <p className="text-xs text-yellow-600/80">
                  aguardando conversão
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Valor Total</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(data.totalDealValue)}</p>
                <p className="text-xs text-muted-foreground">
                  {formatPercent(data.directConversionRate)} taxa direta
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="top" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Top Indicadores
          </TabsTrigger>
          <TabsTrigger value="effort" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Esforço vs Resultado
          </TabsTrigger>
          <TabsTrigger value="conversion" className="flex items-center gap-2">
            <RefreshCcw className="h-4 w-4" />
            Conversão
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Top Indicadores */}
        <TabsContent value="top" className="space-y-6">
          {/* Podium */}
          {data.topReferrersByVolume.length >= 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  Pódio - Top Indicadores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-center gap-4 h-48">
                  {/* 2nd Place */}
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-24 bg-gray-200 dark:bg-gray-700 rounded-t-lg flex flex-col items-center justify-end p-3 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      style={{ height: '120px' }}
                      onClick={() => handleViewContact(data.topReferrersByVolume[1].referrer_contact_id)}
                    >
                      <Medal className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-xs font-medium text-center truncate w-full">
                        {data.topReferrersByVolume[1].referrer_name.split(' ')[0]}
                      </span>
                      <span className="text-lg font-bold">{data.topReferrersByVolume[1].total_referrals}</span>
                    </div>
                    <div className="bg-gray-300 dark:bg-gray-600 w-24 h-8 flex items-center justify-center">
                      <span className="font-bold text-lg">2</span>
                    </div>
                  </div>

                  {/* 1st Place */}
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-28 bg-yellow-100 dark:bg-yellow-900/30 rounded-t-lg flex flex-col items-center justify-end p-3 cursor-pointer hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors"
                      style={{ height: '160px' }}
                      onClick={() => handleViewContact(data.topReferrersByVolume[0].referrer_contact_id)}
                    >
                      <Trophy className="h-10 w-10 text-yellow-500 mb-2" />
                      <span className="text-sm font-medium text-center truncate w-full">
                        {data.topReferrersByVolume[0].referrer_name.split(' ')[0]}
                      </span>
                      <span className="text-2xl font-bold">{data.topReferrersByVolume[0].total_referrals}</span>
                    </div>
                    <div className="bg-yellow-400 dark:bg-yellow-600 w-28 h-8 flex items-center justify-center">
                      <span className="font-bold text-lg">1</span>
                    </div>
                  </div>

                  {/* 3rd Place */}
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-24 bg-amber-100 dark:bg-amber-900/30 rounded-t-lg flex flex-col items-center justify-end p-3 cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                      style={{ height: '100px' }}
                      onClick={() => handleViewContact(data.topReferrersByVolume[2].referrer_contact_id)}
                    >
                      <Award className="h-6 w-6 text-amber-600 mb-2" />
                      <span className="text-xs font-medium text-center truncate w-full">
                        {data.topReferrersByVolume[2].referrer_name.split(' ')[0]}
                      </span>
                      <span className="text-lg font-bold">{data.topReferrersByVolume[2].total_referrals}</span>
                    </div>
                    <div className="bg-amber-400 dark:bg-amber-700 w-24 h-8 flex items-center justify-center">
                      <span className="font-bold text-lg">3</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ranking Table with Expandable Rows */}
          <Card>
            <CardHeader>
              <CardTitle>Ranking Completo</CardTitle>
            </CardHeader>
            <CardContent>
              {data.topReferrersByVolume.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nenhuma indicação encontrada no período
                </p>
              ) : (
                <div className="space-y-1">
                  {/* Header */}
                  <div className="grid grid-cols-[40px_1fr_80px_80px_80px_80px_100px_40px] gap-2 px-4 py-2 text-xs font-medium text-muted-foreground border-b">
                    <div>#</div>
                    <div>Indicador</div>
                    <div className="text-center">Indicações</div>
                    <div className="text-center flex items-center justify-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      Diretas
                    </div>
                    <div className="text-center flex items-center justify-center gap-1">
                      <RefreshCcw className="h-3 w-3 text-blue-500" />
                      Indiretas
                    </div>
                    <div className="text-center">Taxa Dir.</div>
                    <div className="text-right">Valor Total</div>
                    <div></div>
                  </div>

                  {/* Rows */}
                  {data.topReferrersByVolume.map((referrer, index) => {
                    const directRate = referrer.total_referrals > 0 
                      ? (referrer.direct_conversions / referrer.total_referrals) * 100 
                      : 0;
                    const isExpanded = expandedReferrers.has(referrer.referrer_contact_id);
                    const hasConversions = referrer.conversion_details.length > 0;

                    return (
                      <Collapsible 
                        key={referrer.referrer_contact_id}
                        open={isExpanded}
                        onOpenChange={() => hasConversions && toggleExpanded(referrer.referrer_contact_id)}
                      >
                        <CollapsibleTrigger asChild disabled={!hasConversions}>
                          <div 
                            className={`grid grid-cols-[40px_1fr_80px_80px_80px_80px_100px_40px] gap-2 px-4 py-3 items-center hover:bg-muted/50 rounded-lg transition-colors ${hasConversions ? 'cursor-pointer' : ''}`}
                          >
                            <div>{getRankIcon(index)}</div>
                            <div className="font-medium truncate">{referrer.referrer_name}</div>
                            <div className="text-center font-semibold">{referrer.total_referrals}</div>
                            <div className="text-center">
                              <Badge 
                                variant="secondary" 
                                className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 cursor-pointer"
                              >
                                {referrer.direct_conversions}
                              </Badge>
                            </div>
                            <div className="text-center">
                              <Badge 
                                variant="secondary" 
                                className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 cursor-pointer"
                              >
                                {referrer.indirect_conversions}
                              </Badge>
                            </div>
                            <div className="text-center text-sm">{formatPercent(directRate)}</div>
                            <div className="text-right font-medium text-sm">{formatCurrency(referrer.total_deal_value)}</div>
                            <div className="flex items-center gap-1">
                              {hasConversions && (
                                isExpanded ? (
                                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                )
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewContact(referrer.referrer_contact_id);
                                }}
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="mx-4 mb-3 border rounded-lg bg-muted/20 overflow-hidden">
                            <div className="p-3 border-b bg-muted/30">
                              <span className="text-sm font-medium">
                                Conversões de {referrer.referrer_name.split(' ')[0]} ({referrer.conversion_details.length})
                              </span>
                            </div>
                            {renderConversionDetails(referrer)}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Esforço vs Resultado */}
        <TabsContent value="effort" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Esforço vs Resultado
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.effortVsResultData.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum dado disponível para o gráfico
                </p>
              ) : (
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        type="number" 
                        dataKey="referrals" 
                        name="Indicações"
                        domain={[0, 'auto']}
                      >
                        <Label value="Quantidade de Indicações (Esforço)" position="bottom" offset={20} />
                      </XAxis>
                      <YAxis 
                        type="number" 
                        dataKey="deal_value" 
                        name="Valor"
                        tickFormatter={(value) => formatCurrency(value)}
                        domain={[0, 'auto']}
                      >
                        <Label value="Valor Total dos Deals ($)" angle={-90} position="left" offset={40} />
                      </YAxis>
                      <ZAxis 
                        type="number" 
                        dataKey="conversion_rate" 
                        range={[50, 400]} 
                        name="Taxa de Conversão"
                      />
                      <Tooltip 
                        cursor={{ strokeDasharray: '3 3' }}
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const data = payload[0].payload as ScatterDataPoint;
                          return (
                            <div className="bg-popover border rounded-lg p-3 shadow-lg">
                              <p className="font-semibold">{data.referrer_name}</p>
                              <p className="text-sm text-muted-foreground">
                                Indicações: {data.referrals}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Valor: {formatCurrency(data.deal_value)}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Conversão: {formatPercent(data.conversion_rate)}
                              </p>
                            </div>
                          );
                        }}
                      />
                      <ReferenceLine x={avgReferrals} stroke="hsl(var(--muted-foreground))" strokeDasharray="5 5" />
                      <ReferenceLine y={avgValue} stroke="hsl(var(--muted-foreground))" strokeDasharray="5 5" />
                      <Scatter data={data.effortVsResultData} name="Indicadores">
                        {data.effortVsResultData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={quadrantColors[getQuadrant(entry)]}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleViewContact(entry.referrer_contact_id)}
                          />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quadrant Legend */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4" style={{ borderLeftColor: quadrantColors['super-partners'] }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4" style={{ color: quadrantColors['super-partners'] }} />
                  <span className="font-semibold text-sm">Super Parceiros</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Alto esforço + Alto resultado. Premiar e manter engajados!
                </p>
                <p className="text-lg font-bold mt-2">
                  {data.effortVsResultData.filter(d => getQuadrant(d) === 'super-partners').length}
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4" style={{ borderLeftColor: quadrantColors['need-help'] }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCcw className="h-4 w-4" style={{ color: quadrantColors['need-help'] }} />
                  <span className="font-semibold text-sm">Precisam de Ajuda</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Alto esforço + Baixo resultado. Melhorar qualificação.
                </p>
                <p className="text-lg font-bold mt-2">
                  {data.effortVsResultData.filter(d => getQuadrant(d) === 'need-help').length}
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4" style={{ borderLeftColor: quadrantColors['quality'] }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="h-4 w-4" style={{ color: quadrantColors['quality'] }} />
                  <span className="font-semibold text-sm">Alta Qualidade</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Baixo esforço + Alto resultado. Indicações assertivas!
                </p>
                <p className="text-lg font-bold mt-2">
                  {data.effortVsResultData.filter(d => getQuadrant(d) === 'quality').length}
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4" style={{ borderLeftColor: quadrantColors['inactive'] }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4" style={{ color: quadrantColors['inactive'] }} />
                  <span className="font-semibold text-sm">Inativos</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Baixo esforço + Baixo resultado. Reativar relacionamento.
                </p>
                <p className="text-lg font-bold mt-2">
                  {data.effortVsResultData.filter(d => getQuadrant(d) === 'inactive').length}
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab 3: Conversão */}
        <TabsContent value="conversion" className="space-y-6">
          {/* Funnel Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent" />
              <CardContent className="p-6 relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Indicados</p>
                    <p className="text-4xl font-bold">{data.totalReferrals}</p>
                    <p className="text-sm text-muted-foreground">100%</p>
                  </div>
                  <Users className="h-12 w-12 text-primary/50" />
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border-green-500/30">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-transparent" />
              <CardContent className="p-6 relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Conversões Diretas</p>
                    <p className="text-4xl font-bold text-green-600">{data.directConversions}</p>
                    <p className="text-sm text-green-600">{formatPercent(data.directConversionRate)} · {formatCurrency(data.directDealValue)}</p>
                  </div>
                  <CheckCircle2 className="h-12 w-12 text-green-500/50" />
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border-blue-500/30">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent" />
              <CardContent className="p-6 relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Conversões Indiretas</p>
                    <p className="text-4xl font-bold text-blue-600">{data.indirectConversions}</p>
                    <p className="text-sm text-blue-600">
                      {formatPercent(data.totalReferrals > 0 ? (data.indirectConversions / data.totalReferrals) * 100 : 0)} · {formatCurrency(data.indirectDealValue)}
                    </p>
                  </div>
                  <RefreshCcw className="h-12 w-12 text-blue-500/50" />
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border-yellow-500/30">
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 to-transparent" />
              <CardContent className="p-6 relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pendentes</p>
                    <p className="text-4xl font-bold text-yellow-600">{data.pendingConversion}</p>
                    <p className="text-sm text-yellow-600">{formatPercent(data.totalReferrals > 0 ? (data.pendingConversion / data.totalReferrals) * 100 : 0)} do total</p>
                  </div>
                  <Clock className="h-12 w-12 text-yellow-500/50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Legend explaining direct vs indirect */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Conversão Direta</p>
                    <p className="text-xs text-muted-foreground">
                      Deal fechado com fonte "Customer Referral" e sem reativação. 
                      <span className="text-green-600 font-medium"> Conta para comissão.</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <RefreshCcw className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Conversão Indireta</p>
                    <p className="text-xs text-muted-foreground">
                      Contato indicado, mas fechou por outro canal (Paid Ads, Organic, etc) ou foi reativação.
                      <span className="text-blue-600 font-medium"> Apenas reconhecimento.</span>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Referrals Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Indicações Pendentes de Conversão</span>
                <Badge variant="outline">{data.pendingReferrals.length} pendentes</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.pendingReferrals.length === 0 ? (
                <div className="text-center py-8">
                  <UserPlus className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Todas as indicações foram convertidas!
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contato Indicado</TableHead>
                      <TableHead>Indicado Por</TableHead>
                      <TableHead>Data da Indicação</TableHead>
                      <TableHead className="text-center">Tem Deal?</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.pendingReferrals.map((referral) => (
                      <TableRow key={referral.id}>
                        <TableCell className="font-medium">{referral.referred_name}</TableCell>
                        <TableCell className="text-muted-foreground">{referral.referrer_name}</TableCell>
                        <TableCell>
                          {safeFormat(new Date(referral.referral_date), 'dd/MM/yyyy', { locale: ptBR })}
                        </TableCell>
                        <TableCell className="text-center">
                          {referral.has_deal ? (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              Sim
                            </Badge>
                          ) : (
                            <Badge variant="outline">Não</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewContact(referral.referred_contact_id)}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
